// src/modules/app-views/toolbox/pages/Document/tabs/RecordTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { Card, Col, Row, Form } from 'antd';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import translateX from 'utils/translateX';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleCascader from 'components/SimpleComponents/SimpleCascader';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import useZustand from 'hooks/useZustand';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ToolboxRecordTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedParameters = useZustand('app', 'parameters');
  const storedModule = useZustand('module', 'config');
  const { data, loading, _id: documentId } = useDocumentContext() || {};
  const { form } = useSimpleFormContext() || {};

  // Watch current unitUid and toolkitUid to show removed items if tool is using them
  const currentUnitUid = Form.useWatch('unitUid', form);
  const currentToolkitUid = Form.useWatch('toolkitUid', form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const unitsOptions = useMemo(
    () => buildUnitsOptions(storedParameters, storedModule?.id, documentId, currentUnitUid),
    [storedParameters, storedModule?.id, documentId, currentUnitUid]
  );

  const categoriesOptions = useMemo(
    () => buildCategoriesOptions(storedParameters, storedModule?.id),
    [storedParameters, storedModule?.id]
  );

  const toolkitsOptions = useMemo(
    () => buildToolkitsOptions(storedParameters, storedModule?.id, documentId, currentToolkitUid),
    [storedParameters, storedModule?.id, documentId, currentToolkitUid]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="container" style={{ position: 'relative', minHeight: 120 }}>
      <SimpleLoadingArea name="toolboxRecord" isFetching={loading} data={data} />

      {/* TOOL DATA CARD */}
      <Card title={<div className="text-muted">{translateX('record')}</div>}>
        <Row gutter={ROW_GUTTER}>
          <Col xs={24} sm={6} lg={4}>
            <SimpleInput name="sku" tooltip="auto_generated" />
          </Col>

          <Col xs={24} sm={18} lg={10}>
            <SimpleInput name="name" label="name" required />
          </Col>

          <Col xs={24} sm={12} lg={5}>
            <SimpleInput name="brand" label="brand" />
          </Col>

          <Col xs={24} sm={12} lg={5}>
            <SimpleInput name="model" label="model" />
          </Col>
        </Row>

        <Row gutter={ROW_GUTTER}>
          <Col xs={24}>
            <SimpleInput name="description" label="description" type="textarea" rows={4} />
          </Col>
        </Row>

        <Row gutter={ROW_GUTTER}>
          <Col xs={24} sm={12}>
            <SimpleCascader
              name="categoryUids"
              label="category"
              options={categoriesOptions}
              placeholder="select_category"
              showAddButton={{ modal: 'parameters', tab: 'categories' }}
            />
          </Col>

          <Col xs={24} sm={6}>
            <SimpleSelect
              name="unitUid"
              label="unit"
              allowClear
              showSearch
              options={unitsOptions}
              showAddButton={{ modal: 'parameters', tab: 'units' }}
            />
          </Col>

          <Col xs={24} sm={6}>
            <SimpleSelect
              name="toolkitUid"
              label="toolkit"
              allowClear
              showSearch
              options={toolkitsOptions}
              showAddButton={{ modal: 'parameters', tab: 'toolkits' }}
            />
          </Col>
        </Row>
      </Card>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ToolboxRecordTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildUnitsOptions(storedParameters, moduleId, documentId, currentUnitUid) {
  const unitsArray = storedParameters?.modules?.[moduleId]?.units;

  if (!unitsArray || !Array.isArray(unitsArray)) return [];

  const isNewDocument = !documentId || documentId === 'add';

  const filteredUnits = unitsArray.filter(item => {
    if (!item.removed) return true;
    if (isNewDocument) return false;
    return item.uid === currentUnitUid;
  });

  return filteredUnits.map(item => {
    const name = item.name || '';
    const description = item.description || '';
    const displayLabel = description ? `${name} - ${description}` : name;

    return {
      value: item.uid,
      text: displayLabel,
      label: name,
      disabled: false,
    };
  });
}

function buildCategoriesOptions(storedParameters, moduleId) {
  const rawCategories = storedParameters?.modules?.[moduleId]?.categories;

  if (!rawCategories || !Array.isArray(rawCategories) || rawCategories.length === 0) {
    return [];
  }

  // Transform categories to Cascader format (value, label, children)
  return transformCategoriesToCascader(rawCategories);
}

function transformCategoriesToCascader(categories) {
  if (!categories || !Array.isArray(categories)) return [];

  return categories.map(cat => ({
    value: cat.uid,
    label: cat.label,
    children: cat.children?.length > 0 ? transformCategoriesToCascader(cat.children) : undefined,
  }));
}

function buildToolkitsOptions(storedParameters, moduleId, documentId, currentToolkitUid) {
  const toolkitsArray = storedParameters?.modules?.[moduleId]?.toolkits;

  if (!toolkitsArray || !Array.isArray(toolkitsArray)) return [];

  const isNewDocument = !documentId || documentId === 'add';

  const filteredToolkits = toolkitsArray.filter(item => {
    if (!item.removed) return true;
    if (isNewDocument) return false;
    return item.uid === currentToolkitUid;
  });

  return filteredToolkits.map(item => {
    const name = item.name || '';
    const description = item.description || '';
    const displayLabel = description ? `${name} - ${description}` : name;

    return {
      value: item.uid,
      text: displayLabel,
      label: name,
      disabled: false,
    };
  });
}
